import React, { useState, useEffect, useContext } from "react";
import { TouchableOpacity, StyleSheet, ScrollView, TextInput } from "react-native";
import { Text, View } from "../../../components/shared/Themed";
import {
  headers,
  MediumDeviceHeaders,
  SmallDeviceHeaders,
} from "./createUserHeaders";
import Modal from "../../../components/custom/Modal";
import CreateOktaBypassUserAccountForm from "./CreateOktaBypassUserAccountForm";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { NestedDataTable } from "../../../components/custom/DataTable/NestedDataTableWithPagination2";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllUsers,
  searchAllUsers,
} from "../../../redux/dispatcher/usersDispatcher";
import Icon from "react-native-vector-icons/Feather";
import { FontAwesome } from "@expo/vector-icons";

export default function CreateUserSettingsScreen({ navigation }: any) {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const [isRefresh, setRefresh] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { currentCompany } = useSelector((state: any) => state.configurations);

  const { loading, users } = useSelector((state: any) => state.users);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );
  const fetchUsers = () => {
    dispatch(getAllUsers(currentCompany._id || ""));
  };
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );

  useEffect(() => {
    setRefresh(false);
    let isMounted = true;

    if (isMounted) {
      fetchUsers();

      setTableHeaders(
        isLargeDevice
          ? headers
          : isMediumDevice
            ? MediumDeviceHeaders
            : SmallDeviceHeaders
      );
    }

    return () => {
      isMounted = false;
    };
  }, [isRefresh]);

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const filteredUsers = users?.filter((user: any) => {
    if (!searchText || searchText.trim() === "") return true;
    const searchLower = searchText.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.team?.toLowerCase().includes(searchLower) ||
      user.roles?.[0]?.role?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const navigateToOrder = (userId: string, mode: string) => {
    navigation.navigate("BypassUserDetails", { mode: mode, userId: userId });
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={[
        styles.contentContainer,
        isSmallDevice && { paddingHorizontal: 14 },
      ]}
    >
      {/* Header */}
      <View style={[styles.pageHeader, isSmallDevice && { paddingHorizontal: 14, marginHorizontal: -14 }]}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {isSmallDevice && (
            <TouchableOpacity
              onPress={() => navigation.navigate("SettingsM")}
              style={styles.backButton}
            >
              <Icon name="arrow-left" size={18} color="#2563EB" />
            </TouchableOpacity>
          )}
          <View style={{ flex: 1, paddingRight: isSmallDevice ? 10 : 0 }}>
            <Text style={[styles.pageTitle, isSmallDevice && { fontSize: 20 }]} numberOfLines={1}>
              User Management
            </Text>
            <Text style={[styles.pageSubtitle, isSmallDevice && { fontSize: 12 }]} numberOfLines={1}>
              Manage team members and roles
            </Text>
          </View>
        </View>
      </View>


      {/* Toolbar */}
      <View style={[styles.toolbar, isSmallDevice && { flexDirection: "column", gap: 10 }]}>
        <View style={[styles.searchRow, isSmallDevice && { width: "100%" }]}>
          <View style={styles.searchContainer}>
            <Icon name="search" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search users..."
              placeholderTextColor="#9CA3AF"
              value={searchText}
              onChangeText={handleSearch}
            />
          </View>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={() => setRefresh(true)}
          >
            <Icon name="refresh-cw" size={15} color="#6B7280" />
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          style={[styles.addButton, isSmallDevice && { alignSelf: "flex-start" }]}
          onPress={() =>
            navigation.navigate("CreateUser", { screen: "AddUserScreen" })
          }
        >
          <FontAwesome name="plus" size={12} color="#fff" style={{ marginRight: 6 }} />
          <Text style={styles.addButtonText}>Add User</Text>
        </TouchableOpacity>
      </View>

      {/* Table */}
      <View style={styles.tableContainer}>
        <NestedDataTable
          itemsPerPage={15}
          isNestedDataLoaded={loading}
          nestedHeader={tableHeaders}
          nestedRows={filteredUsers}
          key="rowKeyValue"
          userActions={navigateToOrder}
          userActionsKey="email"
        />
      </View>

      <Modal.ModalContainer
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        childern={CreateOktaBypassUserAccountForm}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  contentContainer: {
    paddingHorizontal: 24,
    paddingBottom: 48,
  },

  /* Header */
  pageHeader: {
    paddingTop: 20,
    paddingBottom: 16,
    marginBottom: 14,
    paddingHorizontal: 24,
    marginHorizontal: -24,
    backgroundColor: "#ffffff",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  backButton: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 1,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
  },
  pageSubtitle: {
    fontSize: 13,
    color: "#6B7280",
    marginTop: 2,
  },

  /* Toolbar */
  toolbar: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 14,
    gap: 10,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
  },
  searchContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: "#111827",
    height: 40,
    outlineStyle: "none",
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#2563EB",
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    shadowColor: "#2563EB",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 2,
  },
  addButtonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },

  /* Table */
  tableContainer: {
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    overflow: "hidden",
  },
});
